// File: api
// Created: 2026-05-29
// Updated: 2026-05-29
// Author: Erick Hernández Silva

import { API_URL } from '../config.js';

// Prod API by default; the preview build points at the dev stage (see config.js).
const REMOTE_API = API_URL;

// On localhost the Vite dev server proxies "/api" -> REMOTE_API, which sidesteps
// CORS (the API Gateway does not send Access-Control-Allow-Origin for PATCH).
// In production (Webflow embed) we hit the API directly.
const isLocalDev =
  typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

const API_BASE_URL = isLocalDev ? '/api' : REMOTE_API;

export const fetchVehicleConfiguration = async (vehicleId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/images/${vehicleId}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching vehicle configuration:', error);
    throw error;
  }
};

export const fetchCar = async (id, createdAt) => {
  if (!id || !createdAt) {
    throw new Error('Both id and createdAt are required to fetch a car');
  }

  const url = `${API_BASE_URL}/cars/${encodeURIComponent(id)}/${encodeURIComponent(createdAt)}`;
  console.log('🌐 Fetching car from:', url);

  try {
    const response = await fetch(url);
    console.log('📡 Response status:', response.status);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('📦 Response data:', data);

    if (!data.success || !data.car) {
      throw new Error('API returned unsuccessful response when fetching car');
    }

    return data.car;
  } catch (error) {
    console.error('❌ Error fetching car:', error);
    throw error;
  }
};

export const listCars = async (nextToken = null) => {
  const url = nextToken
    ? `${API_BASE_URL}/cars?nextToken=${encodeURIComponent(nextToken)}`
    : `${API_BASE_URL}/cars`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  if (!data.success) {
    throw new Error('API returned unsuccessful response when listing cars');
  }
  return { items: data.items || [], nextToken: data.nextToken ?? null };
};

// Published cars for the model picker (splash).
export const fetchShowroom = async () => {
  const response = await fetch(`${API_BASE_URL}/showroom`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  if (!data.success) {
    throw new Error('API returned unsuccessful response when listing showroom');
  }
  return data.items || [];
};

export const fetchAllCars = async () => {
  const all = [];
  let nextToken = null;
  do {
    const page = await listCars(nextToken);
    all.push(...page.items);
    nextToken = page.nextToken;
  } while (nextToken);
  return all;
};

export const updateCar = async (id, createdAt, patch) => {
  if (!id || !createdAt) {
    throw new Error('Both id and createdAt are required to update a car');
  }

  const url = `${API_BASE_URL}/cars/${encodeURIComponent(id)}/${encodeURIComponent(createdAt)}`;
  const response = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const details = Array.isArray(data.details) ? `: ${data.details.join('; ')}` : '';
    throw new Error((data.error || `HTTP error! status: ${response.status}`) + details);
  }
  if (!data.success || !data.car) {
    throw new Error('API returned unsuccessful response when updating car');
  }
  return data.car;
};

// --- History (versions) and staging → production publish ---

const carPath = (id, createdAt) => `${API_BASE_URL}/cars/${encodeURIComponent(id)}/${encodeURIComponent(createdAt)}`;

export const fetchCarVersions = async (id, createdAt) => {
  const response = await fetch(`${carPath(id, createdAt)}/versions`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success) throw new Error(data.error || `HTTP error! status: ${response.status}`);
  return data.items || [];
};

export const fetchCarVersion = async (id, createdAt, versionAt) => {
  const response = await fetch(`${carPath(id, createdAt)}/versions/${encodeURIComponent(versionAt)}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success) throw new Error(data.error || `HTTP error! status: ${response.status}`);
  return { version: data.version, car: data.car };
};

/** Staging only. Resolves { ok, status, data } so callers can handle 409 (prod is newer). */
export const publishCarToProd = async (id, createdAt, { key, note, force = false }) => {
  const response = await fetch(`${carPath(id, createdAt)}/publish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, note, force }),
  });
  const data = await response.json().catch(() => ({}));
  return { ok: response.ok && !!data.success, status: response.status, data };
};

// --- Save & Share (configurations) ---

export const createConfiguration = async (payload) => {
  const response = await fetch(`${API_BASE_URL}/configurations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const details = Array.isArray(data.details) ? `: ${data.details.join('; ')}` : '';
    throw new Error((data.error || `HTTP error! status: ${response.status}`) + details);
  }
  if (!data.success || !data.configuration) {
    throw new Error('API returned unsuccessful response when creating configuration');
  }
  return data.configuration;
};

export const getConfiguration = async (id, includeCar = false) => {
  if (!id) throw new Error('id is required to fetch a configuration');
  const url = `${API_BASE_URL}/configurations/${encodeURIComponent(id)}${includeCar ? '?includeCar=true' : ''}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  if (!data.success || !data.configuration) {
    throw new Error('API returned unsuccessful response when fetching configuration');
  }
  return { configuration: data.configuration, car: data.car || null };
};

export const uploadImage = async (file, folder = null, carId = null) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (folder) {
      formData.append('folder', folder);
    }
    if (carId) {
      formData.append('carId', carId);
    }

    const response = await fetch(`${API_BASE_URL}/upload-file`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success || !data.url) {
      throw new Error('API returned unsuccessful response when uploading image');
    }

    return data;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

export const createCar = async (carPayload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/cars`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(carPayload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success || !data.car) {
      throw new Error('API returned unsuccessful response when creating car');
    }

    return data;
  } catch (error) {
    console.error('Error creating car:', error);
    throw error;
  }
};

export const getFeatureOptions = (features, featureName) => {
  const feature = features.find(f => f.feature === featureName);
  return feature ? feature.images : [];
};

export const getDefaultSelections = (features) => {
  const selections = {};
  features.forEach(feature => {
    if (feature.images && feature.images.length > 0) {
      selections[feature.feature] = feature.images[0].filename;
    }
  });
  return selections;
};
